import { FlexBox, IconButton, Text } from '@dixa/lib-shared-habla';
import { type ChatStatus } from 'ai';
import classnames from 'classnames';
import { KeyboardEvent, useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import styles from './mimSidekickComposer.module.scss';

type MimSidekickComposerProps = {
  status: ChatStatus;
  onSend: (text: string) => void;
  onStop: () => void;
};

type FormValues = {
  prompt: string;
};

export const MimSidekickComposer = ({
  status,
  onSend,
  onStop,
}: MimSidekickComposerProps): JSX.Element => {
  const { register, handleSubmit, getValues, setValue, setFocus, watch } =
    useForm<FormValues>({
      mode: 'all',
      defaultValues: {
        prompt: '',
      },
    });

  const prompt = watch('prompt');
  const isGenerating = status === 'submitted' || status === 'streaming';
  const hasError = status === 'error';

  const lastSentRef = useRef('');

  // Only a failed send from this composer is restored.
  useEffect(() => {
    if (status === 'ready') lastSentRef.current = '';
    if (status === 'error' && !getValues('prompt')) {
      setValue('prompt', lastSentRef.current);
    }
  }, [status, getValues, setValue]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleValidSubmit({ prompt });
    }
  };

  const handleValidSubmit: SubmitHandler<FormValues> = (values) => {
    const { prompt } = values;

    const trimmed = prompt.trim();
    if (!trimmed || isGenerating) return;

    lastSentRef.current = trimmed;
    onSend(trimmed);
    setValue('prompt', '');
  };

  return (
    <div className={styles.root}>
      <form
        className={classnames(styles.composer, { [styles.error]: hasError })}
        onSubmit={handleSubmit(handleValidSubmit)}
        onClick={() => setFocus('prompt')}
      >
        <textarea
          {...register('prompt')}
          className={styles.input}
          placeholder={
            isGenerating ? 'Sidekick is responding...' : 'Ask Sidekick...'
          }
          disabled={isGenerating}
          onKeyDown={handleKeyDown}
          dir="auto"
        />
        <FlexBox justifyContent="flex-end">
          {isGenerating ? (
            <IconButton
              icon="stop"
              variant="tertiary"
              aria-label="Stop"
              className={styles.action}
              onClick={onStop}
            />
          ) : (
            <IconButton
              icon="paper-plane"
              variant="tertiary"
              type="submit"
              aria-label="Send"
              className={styles.action}
              disabled={prompt.trim().length === 0}
            />
          )}
        </FlexBox>
      </form>
      {hasError && (
        <Text.Paragraph variant="4" color="error">
          Message not sent. Check your connection and try again.
        </Text.Paragraph>
      )}
    </div>
  );
};
